<template>
  <div class="page-container package-page">
    <portal to="page-name">Cadastro de pacote</portal>
    <div class="card card-page">
      <div class="card-header">
        <div class="form-group float-right status-toggle" v-if="!loading">
          <label class="form-switch">
            <input type="checkbox" v-model="form.active">
            <i class="form-icon"></i>Registro ativo
          </label>
        </div>
        <h1 class="card-title">Cadastro de pacote</h1>
      </div>
      <div class="card-body">
        <div class="loading loading-lg" v-if="loading" />
        <template v-else>
          <div class="columns">
            <div class="column col-8 form-group"
                 :class="{'has-error': $v.form.name.$error}">
              <label for="name" class="form-label">Nome</label>
              <input type="text" v-focus
                     class="form-input"
                     v-model="form.name"
                     @change="$v.form.name.$touch()">
              <template v-if="$v.form.name.$error">
                <div class="form-input-hint"
                     v-if="!$v.form.name.required">Campo obrigatório</div>
              </template>
            </div>
            <div class="column col-4 col-md-6 col-sm-12 form-group">
              <label for="type" class="form-label">Tipo</label>
              <select class="form-select"
                      v-model="form.type">
                <option v-for="(text, value) in placeType.places"
                        :value="value" :key="value">{{ text }}</option>
              </select>
            </div>
          </div>
          <h6 class="text-secondary mt-2">Procedimentos</h6>
          <div class="divider" />
          <div class="form-group">
            <dx-autocomplete
              v-model="expense"
              :source="findExpense"
              label="name"
              track-by="id"
              :debounce="800"
              @select="addExpense"
              placeholder="Informe o código ou nome do procedimento..."
              input-id="expense-name"
            >
              <template v-slot="{ item }">
                <a>
                  <fa-icon :icon="['fal', 'file-medical-alt']" class="text-primary"/>
                  <span class="ml-1">
                   {{ item.name }}
                  <span v-if="item.tuss?.code">({{ item.tuss.code | tuss }})</span>
                </span>
                </a>
              </template>
              <template v-slot:action>
                <button
                  type="button"
                  class="btn btn-action input-group-btn btn-icon btn-neutral"
                ><fa-icon :icon="['fal', 'search']" /></button>
              </template>
            </dx-autocomplete>
          </div>
          <div class="empty mt-2" v-if="form.expenses.length === 0">
            <div class="empty-icon">
              <fa-icon :icon="['fal', 'info-circle']" size="3x"/>
            </div>
            <p class="empty-title h5">Procedimentos</p>
            <p class="empty-subtitle">
              Nenhum procedimento adicionado. Realize a busca utilizando o campo acima.
            </p>
          </div>
          <table class="table" v-else>
            <tr>
              <th>#</th>
              <th>Código</th>
              <th>Nome</th>
              <th>Tipo</th>
              <th class="text-center" style="width: 100px">Quantidade</th>
              <th style="width: 50px"></th>
            </tr>
            <tr v-for="(item, i) in form.expenses" :key="i">
              <td>{{ i + 1 }}</td>
              <td>{{ item.code | tuss }}</td>
              <td>{{ item.name }}</td>
              <td>{{ getName(item.type) }}</td>
              <td class="text-center">
                <dx-input-number
                  class="form-input text-center"
                  v-model="item.quantity"
                  @blur="setQuantity(item)"
                  maxlength="4"
                />
              </td>
              <td class="text-right">
                <button class="btn btn-sm btn-action btn-icon btn-error"
                        @click="removeExpense(i)">
                  <fa-icon :icon="['fal', 'times']"></fa-icon>
                </button>
              </td>
            </tr>
          </table>
        </template>
      </div>
      <div class="card-footer">
        <button class="btn btn-primary mr-1"
                v-if="!loading"
                :class="{loading: saving}"
                :disabled="saving"
                @click="save">
          Salvar
        </button>
        <button class="btn" @click="$router.back()">
          Voltar
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { required } from 'vuelidate/src/validators';
import formMixin from 'src/mixins/form';
import { mergeFrom } from '@/helpers/object';
import * as placeType from 'src/data/place-types';
import { getName } from '@/data/expense-types';

export default {
  mixins: [formMixin],
  data() {
    return {
      placeType,
      isNew: true,
      loading: false,
      saving: false,
      expense: null,
      getName,
      form: this.blankForm(),
    };
  },
  async mounted() {
    this.isNew = /create$/.test(this.$route.path);

    if (!this.isNew) {
      this.form.id = this.$route.params.id;
      this.load();
    }
  },
  validations() {
    return {
      form: {
        name: { required },
        type: { required },
        active: { required },
      },
    };
  },
  methods: {
    load() {
      this.loading = true;

      return this.$http
        .get(`/packages/${this.form.id}`)
        .then(({ data }) => {
          this.form = mergeFrom(this.blankForm(), data);
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    findExpense(search) {
      const params = {
        search,
        limit: 30,
        offset: 0,
      };

      return this.$httpX.get('/expenses', { params })
        .then(({ data }) => {
          if (data.items.length === 0) {
            return [{
              id: null,
              type: null,
              code: null,
              name: 'Procedimento não encontrado!',
              quantity: 0,
            }];
          }
          return data.items;
        });
    },
    addExpense(expense) {
      if (!expense || expense.id === null) {
        return;
      }

      const found = this.form.expenses.find(({ id }) => id === expense.id);

      if (found) {
        found.quantity += 1;
      } else {
        this.form.expenses.push({
          id: expense.id,
          type: expense.type,
          code: expense?.tuss?.code || null,
          name: expense.name,
          quantity: 1,
        });
      }

      this.expense = null;
    },
    removeExpense(i) {
      this.form.expenses.splice(i, 1);
    },
    setQuantity(expense) {
      if (expense.quantity === 0) {
        expense.quantity = 1;
      }
    },
    async save() {
      this.$v.form.$touch();
      if (this.$v.form.$error) {
        return;
      }

      this.saving = true;

      const packageData = this.clone(this.form);

      const request = this.isNew
        ? this.$http.post('/packages', packageData)
        : this.$http.put(`/packages/${this.form.id}`, packageData);

      // eslint-disable-next-line consistent-return
      return request
        .then(({ data }) => {
          if (this.isNew) {
            this.$router.replace(`/clinical/packages/${data.id}/edit`);
            this.isNew = false;
            this.form.id = data.id;
          }
          this.$toast.show('Registro salvo!');
        })
        .catch(() => {})
        .finally(() => {
          this.saving = false;
        });
    },
    blankForm() {
      return {
        id: '',
        type: 'clinical',
        name: '',
        active: true,
        expenses: [],
      };
    },
  },
};
</script>

<style lang="scss">
  .package-page {
  }
</style>
